import React, { FC, useEffect } from 'react';
import { makeUniqueId } from '@apollo/client/utilities';
import dynamic from 'next/dynamic';
import { useHandleSubmit } from './use-handle-submit';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { useGlobalSearchContext } from '../../../store/global-search.context';
import { useGlobalSearchValues, useSetGlobalSearch } from '../../../store/recoil/global-search';
import NotFoundHint from './not-found-hint';
import { useProductDetailsValues } from '../../../store/recoil/product-details';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  isGlobalSearchHeaderActiveState, isPageHeaderSearchDropdownVisibleState,
} from './global-search.state';
import {
  isSearchLocationOverlayOpenState,
} from '../../digando-overlay/search-location-overlay/states/search-location-overlay.states';
import { TermField } from './fields/term-field';
import { LocationField } from './fields/location-field';
import { FromDateField } from './fields/from-date-field';
import { ToDateField } from './fields/to-date-field';
import classNames from 'classnames';
import styles from './global-search-bar.module.scss';
import { PageHeaderInfo } from './page-header-info';
import { GlobalSearchBarSearchButton } from './fields/search-button';

const DynamicSuggestionTerm = dynamic(() => import('./suggestion/suggestion-term'));
const DynamicSuggestionLocation = dynamic(() => import('./suggestion/suggestion-location'));
const DynamicSuggestionDate = dynamic(() => import('./suggestion/suggestion-date'));

interface IGlobalSearchBarProps {
  tenantKey?: string | null;
}

export const GlobalSearchBarInHeader: FC<IGlobalSearchBarProps> = ({
  tenantKey: initialTenantKey = null,
}) => {
  const {
    closeSuggestion,
  } = useGlobalSearchContext();
  const setIsSearchLocationOverlayOpen = useSetRecoilState(isSearchLocationOverlayOpenState);

  const containerRef = useDetectClickOutside({
    onTriggered: (event): void => {
      /** Workaround for re-renderings with dynamic ids of form elements. */
      // check if a parent element has id starting with global-search
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - event is not typed correctly inside useDetectClickOutside
      const foundParent = event?.target?.closest('[id^=global-search], [class^=react-datepicker]');

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - event is not typed correctly inside useDetectClickOutside
      const id = event?.target?.id;

      // Close suggestion if click was outside the search bar, for that we check if the click was not on the search bar or the date picker
      if (!foundParent && id !== 'page-content-wrapper') {
        closeSuggestion();
        setIsSearchLocationOverlayOpen(false);
      }
    },
  });

  return (
    <div ref={containerRef} id='global-search-outer-container'>
      <GlobalSearchBarInsideHeaderComponent
        tenantKey={initialTenantKey}
      />
    </div>
  );
};

export const GlobalSearchBarInsideHeaderComponent: FC<IGlobalSearchBarProps> = ({
  tenantKey: initialTenantKey = null,
}) => {
  const globalSearchEndDateId = makeUniqueId('global-search-end-date-header');

  const { tenantKey } = useGlobalSearchValues();
  const { setTenantKey } = useSetGlobalSearch();
  const { isSuggestionVisible, } = useGlobalSearchContext();

  const [isPageHeaderSearchDropdownVisible, setIsPageHeaderSearchDropdownVisibleState] = useRecoilState(isPageHeaderSearchDropdownVisibleState);
  const { handleSubmitSearch } = useHandleSubmit();
  const { mainSearchVisible } = useGlobalSearchValues();
  const { notFoundHintVisible } = useProductDetailsValues();
  const [isGlobalSearchHeaderActive] = useRecoilState(isGlobalSearchHeaderActiveState);

  useEffect(() => {
    setTenantKey(initialTenantKey);
  }, [initialTenantKey]);

  useEffect(() => {
    mainSearchVisible && setIsPageHeaderSearchDropdownVisibleState(false);
  }, [mainSearchVisible]);

  const suggestionWrapper = (): JSX.Element => {
    if ('undefined' === typeof window || 768 > window.innerWidth) {
      return <></>;
    }

    return (
      <div
        className={classNames({
          [styles.suggestionWrapperHeader]: true,
          [styles.noSpacing]: false,
          [styles.isOpen]: isSuggestionVisible && false === mainSearchVisible,
          [styles.isMainSearchVisible]: false,
        })}
      >
        <DynamicSuggestionTerm />
        <DynamicSuggestionLocation />
        <DynamicSuggestionDate endDateId={globalSearchEndDateId} />
      </div>
    );
  };

  return (
    <div className={styles.pageHeaderGlobalSearchBarContainer}>
      {notFoundHintVisible && <NotFoundHint />}

      <div className={styles.pageHeaderDesktopForm}>
        <form
          onKeyUp={(event): void => {
            if ('Enter' === event.key) {
              handleSubmitSearch();
            }
          }}
        >
          <div className={styles.pageHeaderDesktopFormWrapper}>
            <TermField identifier={'header'} compact={true} />

            <LocationField identifier={'header'} preselectedTenantKey={tenantKey ?? null} compact={true} />

            <div className={styles.fromToFieldWrapper}>
              <FromDateField identifier={'header-desktop'} compact={true} />

              <ToDateField identifier={'header-desktop'} compact={true} />
            </div>

            <div className={styles.searchButtonWrapper}>
              <GlobalSearchBarSearchButton preselectedTenantKey={tenantKey ?? null} ghostButton={true} />
            </div>
          </div>
        </form>
      </div>

      <div className={styles.pageHeaderMobileForm}>
        {notFoundHintVisible && <NotFoundHint />}
        <form
          onKeyUp={(event): void => {
            if ('Enter' === event.key) {
              handleSubmitSearch();
            }
          }}
        >
          <PageHeaderInfo />

          {(isPageHeaderSearchDropdownVisible) && <div className={styles.pageHeaderSearchDropdown}>
            <TermField identifier={'header'} compact={false} />

            <LocationField identifier={'header'} preselectedTenantKey={tenantKey ?? null} compact={false} />

            <div className={styles.fromToFieldWrapper}>
              <FromDateField identifier={'header-mobile'} compact={false} />

              <ToDateField identifier={'header-mobile'} compact={false} />
            </div>

            <div className={styles.searchButtonWrapper}>
              <GlobalSearchBarSearchButton preselectedTenantKey={tenantKey ?? null} />
            </div>
          </div>}

        </form>
      </div>

      {(false === mainSearchVisible && true === isGlobalSearchHeaderActive) && (suggestionWrapper())}
    </div>
  );
};


